import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { NotificationBell } from '@/components/notifications';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { logger } from '@/lib/logger';
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  ClipboardList,
  BarChart3,
  Wallet,
  Bell,
  FolderTree,
  Settings2,
  Activity,
  CreditCard,
  Users,
  History,
  BookOpen,
  TrendingUp,
  Database,
  Upload,
  Settings,
  LogOut,
  ArrowLeftRight,
  Scale,
  HeartPulse,
} from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  title: string;
  icon: React.ReactNode;
  items: NavItem[];
}

// Primary navigation (always visible)
const primaryNav: NavItem[] = [
  { path: '/dashboard', label: '대시보드', icon: <LayoutDashboard className="w-5 h-5" /> },
  { path: '/upload', label: '파일 업로드', icon: <Upload className="w-5 h-5" /> },
  { path: '/review', label: '거래 검수', icon: <ClipboardList className="w-5 h-5" /> },
];

// Collapsible sections
const navSections: NavSection[] = [
  {
    title: '결과 보기',
    icon: <TrendingUp className="w-5 h-5" />,
    items: [
      { path: '/reports', label: '월간 리포트', icon: <BarChart3 className="w-4 h-4" /> },
      { path: '/cash-flow', label: '현금흐름표', icon: <ArrowLeftRight className="w-4 h-4" /> },
      { path: '/balance-sheet', label: '재무상태표', icon: <Scale className="w-4 h-4" /> },
      { path: '/financial-health', label: '재무건전성', icon: <HeartPulse className="w-4 h-4" /> },
      { path: '/budget', label: '예산 현황', icon: <Wallet className="w-4 h-4" /> },
      { path: '/notifications', label: '알림', icon: <Bell className="w-4 h-4" /> },
    ],
  },
  {
    title: '데이터 관리',
    icon: <Database className="w-5 h-5" />,
    items: [
      { path: '/classification?tab=status', label: '분류 시스템 상태', icon: <Activity className="w-4 h-4" /> },
      { path: '/classification?tab=categories', label: '분류 카테고리', icon: <FolderTree className="w-4 h-4" /> },
      { path: '/classification?tab=rules', label: '분류 규칙', icon: <Settings2 className="w-4 h-4" /> },
    ],
  },
  {
    title: '설정',
    icon: <Settings className="w-5 h-5" />,
    items: [
      { path: '/payment-methods', label: '결제수단', icon: <CreditCard className="w-4 h-4" /> },
      { path: '/settings', label: '가족 설정', icon: <Users className="w-4 h-4" /> },
      { path: '/audit-log', label: '변경 이력', icon: <History className="w-4 h-4" /> },
      { path: '/service-guide', label: '도움말', icon: <BookOpen className="w-4 h-4" /> },
    ],
  },
];

// Mobile quick access items (subset of most used)
const mobileQuickNav: NavItem[] = [
  { path: '/dashboard', label: '대시보드', icon: <LayoutDashboard className="w-6 h-6" /> },
  { path: '/upload', label: '업로드', icon: <Upload className="w-6 h-6" /> },
  { path: '/review', label: '검수', icon: <ClipboardList className="w-6 h-6" /> },
  { path: '/reports', label: '리포트', icon: <BarChart3 className="w-6 h-6" /> },
  { path: '/budget', label: '예산', icon: <Wallet className="w-6 h-6" /> },
];

function CollapsibleSection({
  section,
  isExpanded,
  onToggle,
}: {
  section: NavSection;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const location = useLocation();
  // Check if current path matches any item (handle query params)
  const isActive = section.items.some((item) => {
    const [itemPath, itemSearch] = item.path.split('?');
    if (location.pathname !== itemPath) return false;
    // If item has query params, check if current URL includes them
    if (itemSearch) {
      return location.search.includes(itemSearch);
    }
    return true;
  });

  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors',
          isActive
            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
        )}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center">
          <span className="mr-3 text-primary-500" aria-hidden="true">
            {section.icon}
          </span>
          <span>{section.title}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="ml-6 space-y-1 overflow-hidden"
          >
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-primary-100/50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                  )
                }
              >
                <span className="mr-3 text-gray-400 group-hover:text-primary-500 transition-colors" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MainLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Initialize expanded sections based on current path (runs once on mount)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const expanded: Record<string, boolean> = {};
    navSections.forEach((section) => {
      const isActive = section.items.some((item) => {
        const [itemPath, itemSearch] = item.path.split('?');
        if (location.pathname !== itemPath) return false;
        if (itemSearch) {
          return location.search.includes(itemSearch);
        }
        return true;
      });
      expanded[section.title] = isActive;
    });
    return expanded;
  });

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">모닛</h1>
              <span className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-400">주간 재무 에이전트</span>
            </div>

            <div className="flex items-center gap-4" data-testid="user-menu">
              <ThemeToggle />
              {user && (
                <>
                  <NotificationBell />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.displayName || user.email}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">로그아웃</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:flex-shrink-0 sticky top-[73px] h-[calc(100vh-73px)]">
          <div className="w-64 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-r border-gray-200/50 dark:border-gray-700/50">
            <nav className="mt-5 px-3 space-y-4">
              {/* Primary Navigation */}
              <div className="space-y-2">
                {primaryNav.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      clsx(
                        'group flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300',
                        isActive
                          ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30 scale-[1.02]'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-700/80 hover:text-gray-900 dark:hover:text-white'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={clsx("mr-3 transition-colors", isActive ? "text-white" : "text-primary-500 group-hover:text-primary-600")} aria-hidden="true">
                          {item.icon}
                        </span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>

              {/* Divider */}
              <div className="px-4">
                <div className="border-t border-gray-200 dark:border-gray-700 my-4" />
              </div>

              {/* Collapsible Sections */}
              <div className="space-y-1">
                {navSections.map((section) => (
                  <CollapsibleSection
                    key={section.title}
                    section={section}
                    isExpanded={expandedSections[section.title] ?? false}
                    onToggle={() => toggleSection(section.title)}
                  />
                ))}
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Navigation - Fixed bottom bar with quick access */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] pb-safe">
        <div className="flex justify-around items-center py-3">
          {mobileQuickNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                clsx(
                  'flex flex-col items-center py-1 px-2 text-xs rounded-xl transition-all duration-300 min-w-[64px]',
                  isActive ? 'text-primary-600 dark:text-primary-400 font-bold' : 'text-gray-500 dark:text-gray-400'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className={clsx("mb-1 transition-transform duration-300", isActive && "scale-110")}>{item.icon}</span>
                  <span className="whitespace-nowrap text-[10px] tracking-tight">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
